import { usePrivy } from "@privy-io/react-auth";
import {
	CheckCircle2,
	Plus,
	Search,
	Trophy,
	Users,
	Wallet,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDebounceCallback } from "usehooks-ts";
import { Button, buttonVariants } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import IdentityList from "../components/IdentityList";
import { useShinkaiIdentityQuery } from "../hooks/shinkai-identity";
import { useEvmWallet } from "../hooks/useEvmWallet";
import { useGetIdentities } from "../hooks/useGetIdentities";

const EMPTY_OWNER = "0x0000000000000000000000000000000000000000";

function formatStakedKAI(stakedTokens: string) {
	const stakedValue = Number(BigInt(stakedTokens)) / 1e18;
	return stakedValue >= 1000
		? `${(stakedValue / 1000).toFixed(1)}k`
		: stakedValue.toFixed(0);
}

export default function IdentitiesPage() {
	const { authenticated, ready } = usePrivy();
	const { address, isConnected } = useEvmWallet();
	const { data: shinkaiIdentityData } = useShinkaiIdentityQuery();
	const savedRewardsIdentity =
		shinkaiIdentityData?.selectedShinkaiIdentity ?? null;

	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const debounceSearch = useDebounceCallback(
		useCallback((value: string) => setDebouncedSearch(value), []),
		400,
	);

	const ownerAddress = isConnected
		? (address?.toLowerCase() ?? EMPTY_OWNER)
		: EMPTY_OWNER;

	const {
		data: ownedData,
		isLoading: isLoadingOwnedIdentities,
		isError: isOwnedIdentitiesError,
		hasNextPage: hasNextOwnedIdentities,
		isFetchingNextPage: isFetchingNextOwnedIdentities,
		fetchNextPage: fetchNextOwnedIdentities,
	} = useGetIdentities({
		owner: ownerAddress,
	});

	const ownedIdentities = useMemo(
		() =>
			isConnected
				? (ownedData?.pages
						.flat()
						.filter((identity): identity is NonNullable<typeof identity> =>
							Boolean(identity),
						) ?? [])
				: [],
		[isConnected, ownedData],
	);

	const handleSearchChange = (value: string) => {
		setSearch(value);
		debounceSearch(value);
	};

	return (
		<main className="grow pb-20 px-4 sm:px-6 font-inter">
			<div className="max-w-5xl mx-auto space-y-8 pt-8">
				{/* Header Section */}
				<div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
					<div className="space-y-2">
						<h1 className="font-clash text-4xl font-medium text-text-default tracking-tight">
							Identities
						</h1>
						<p className="text-sm text-text-secondary max-w-lg">
							Manage your digital identities on the Shinkai network. Register
							new names, track stakes, and select identities for rewards.
						</p>
					</div>
					<Link
						className={cn(
							buttonVariants({ variant: isConnected ? "default" : "outline" }),
						)}
						to="/register"
					>
						<Plus className="w-4 h-4 mr-2" />
						Register Identity
					</Link>
				</div>

				{/* Tabs Section */}
				<Tabs defaultValue="all" className="w-full space-y-6">
					<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-0">
						{isConnected && (
							<TabsList className="border-none gap-3 bg-transparent p-1 self-start sm:self-auto">
								<TabsTrigger
									value="all"
									className="px-6 rounded-full border bg-bg-transparent data-[state=active]:bg-bg-secondary"
								>
									All Identities
								</TabsTrigger>
								<TabsTrigger
									value="my"
									className="px-6 rounded-full border bg-bg-transparent data-[state=active]:bg-bg-secondary"
								>
									My Identities
								</TabsTrigger>
							</TabsList>
						)}

						{/* Global Search (visible when applicable) */}
						<div
							className={cn(
								"relative w-full rounded-full overflow-hidden border border-divider h-[40px]",
								isConnected ? "sm:w-72" : "sm:w-full",
							)}
						>
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
							<input
								type="search"
								value={search}
								onChange={(e) => handleSearchChange(e.target.value)}
								placeholder="Search identities..."
								className="size-full bg-bg-secondary/50 py-2 pl-9 pr-3 text-text-default placeholder-text-placeholder text-sm focus:outline-none focus:ring-1 focus:ring-brand/50 focus:border-brand/50 transition-all"
							/>
						</div>
					</div>

					<TabsContent value="all" className="mt-0 space-y-4">
						<div className="rounded-2xl border border-divider bg-bg-secondary/10 overflow-hidden p-1">
							<IdentityList externalFilter={debouncedSearch} hideSearch />
						</div>
					</TabsContent>

					<TabsContent value="my" className="mt-0">
						<div className="rounded-2xl border border-divider bg-bg-secondary/10 overflow-hidden min-h-[300px]">
							{!isConnected ? (
								<div className="flex flex-col items-center justify-center py-20 space-y-4">
									<div className="w-12 h-12 rounded-full bg-bg-tertiary flex items-center justify-center">
										<Wallet className="w-6 h-6 text-text-tertiary" />
									</div>
									<div className="text-center space-y-1">
										<p className="text-text-default font-medium">
											Connect Wallet
										</p>
										<p className="text-sm text-text-secondary">
											Please connect your wallet to view your identities
										</p>
									</div>
								</div>
							) : isLoadingOwnedIdentities ? (
								<div className="flex items-center justify-center py-20">
									<div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
								</div>
							) : isOwnedIdentitiesError ? (
								<div className="flex items-center justify-center py-20 text-destructive">
									Failed to load identities
								</div>
							) : ownedIdentities.length === 0 ? (
								<div className="flex flex-col items-center justify-center py-20 space-y-4">
									<div className="w-12 h-12 rounded-full bg-bg-tertiary flex items-center justify-center">
										<Users className="w-6 h-6 text-text-tertiary" />
									</div>
									<div className="text-center space-y-1">
										<p className="text-text-default font-medium">
											No Identities Found
										</p>
										<p className="text-sm text-text-secondary">
											You don't own any identities yet
										</p>
									</div>
									<Link
										className={cn(buttonVariants({ variant: "outline" }))}
										to="/register"
									>
										Register your first identity
									</Link>
								</div>
							) : (
								<div className="p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
									{ownedIdentities.map((identity) => (
										<Link
											key={identity.identityRaw}
											to={`/identity/${encodeURIComponent(identity.identityRaw)}`}
											className="group relative flex flex-col gap-3 rounded-xl border border-divider bg-bg-tertiary/20 p-4 transition-all hover:border-brand/40 hover:bg-bg-tertiary/40 hover:shadow-md hover:shadow-black/20"
										>
											<div className="flex items-start justify-between">
												<div className="w-10 h-10 rounded-full bg-linear-to-br from-bg-tertiary to-bg-secondary border border-divider flex items-center justify-center text-text-secondary font-bold text-lg">
													{identity.identityRaw.charAt(0).toUpperCase()}
												</div>
												{savedRewardsIdentity === identity.identityRaw && (
													<span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full bg-success/10 text-success border border-success/20">
														<CheckCircle2 className="w-3 h-3" />
														Rewards
													</span>
												)}
											</div>

											<div>
												<p className="font-medium text-text-default truncate text-lg">
													{identity.identityRaw}
												</p>
												<p className="text-xs text-text-tertiary mt-1">
													Owner: {formatStakedKAI(identity.stakedTokens)} KAI
													Staked
												</p>
											</div>

											<div className="absolute inset-0 border border-brand/0 rounded-xl transition-all group-hover:border-brand/20 pointer-events-none" />
										</Link>
									))}

									{hasNextOwnedIdentities && (
										<div className="col-span-full flex justify-center pt-4 pb-2">
											<Button
												variant="tertiary"
												onClick={() => fetchNextOwnedIdentities()}
												disabled={isFetchingNextOwnedIdentities}
												className="text-text-secondary hover:text-text-default"
											>
												{isFetchingNextOwnedIdentities
													? "Loading..."
													: "Load more identities"}
											</Button>
										</div>
									)}
								</div>
							)}
						</div>
					</TabsContent>
				</Tabs>
			</div>
		</main>
	);
}
